<template>
	<section
		:aria-labelledby="'cog-' + $source.id"
		class="cog-section cog-repeating-section"
		:class="[
			'cog-col',
			colNumClass,
			{
				'is-error': showError,
				'cog-section--heading': $source.label && !hideLabel,
				'is-read-only': $source.readonly,
				['cog-field--' + fieldIndex]: fieldIndex,
				'cog-force-shown': forceShown,
				'show-remove-button': showRemoveButton,
				'hide-remove-button': !showRemoveButton
			}
		]"
		:tabindex="showValidation ? 0 : -1"
		:aria-describedby="showError ? `cog-${$source.id}-error` : undefined"
		:aria-invalid="showError ? true : undefined"
		@click="triggerErrors"
		@keyup="triggerErrors"
	>
		<div class="cog-section__inner">
			<slot name="header">
				<h2 :is="'h' + Math.min(depth + 1,4)" v-if="showLabel" :id="'cog-' + $source.id" class="cog-section__heading cog-heading">
					{{ $source.label }}
					<span v-if="showError" class="cog-offscreen">, {{ errorMessage }}</span>
				</h2>
				<!-- eslint-disable-next-line vue/no-v-html -->
				<div v-if="$source.helptext && !$source.readonly" class="cog-helptext cog-html" v-html="$source.helptext"></div>
			</slot>
			<list-transition :scroll="allowScroll && flags.autoScroll">
				<c-row v-for="($item, index) in $source.items" :key="$item.value.meta.id">
					<c-section
						v-show="!$item.isHidden"
						ref="repeatingSection"
						:aria-labelledby="`cog-${$source.id}-${index}`"
						:background="(index % 2) ? 'alternate' : 'main'"
						class="cog-repeating-section__section"
						:class="{'cog-section--heading': itemLabel, 'is-read-only': $source.readonly, [`${$item.class}`]: $item.class}" :source="$item"
					>
						<template #header>
							<h3 :is="'h' + Math.min(depth + 2, 4)" :id="`cog-${$source.id}-${index}`" class="cog-repeating-section__heading cog-heading">
								<transition
									appear
									name="cog-fade"
								>
									<c-button
										v-if="showRemoveButton"
										no-style
										class="cog-repeating-section__remove-button"
										:title="getRemoveButtonText(itemLabel, $item.value.ItemNumber)"
										@click="e => removeItem(e, index)"
									>
										<i-ex />
									</c-button>
								</transition>
								<slot name="readonly-item-header" :item="$item.value">
									{{ itemLabel }} {{ $item.value.ItemNumber }}
								</slot>
							</h3>
						</template>
						<slot :item="$item.value" :adapter="$item" :cols="cols"></slot>
					</c-section>
				</c-row>
			</list-transition>
			<slot name="footer"></slot>
			<visible-transition>
				<c-button
					v-if="showAddButton"
					class="cog-button--secondary cog-repeating-section__add-button"
					:class="{'cog-button--no-text': getAddButtonText(itemLabel) === ''}"
					:title="getAddButtonText(itemLabel) === '' ? getGeneratedAddButtonText(itemLabel) : null"
					@click="addItem"
				>
					{{ getAddButtonText(itemLabel) }}
					<template #icon><i-plus /></template>
				</c-button>
			</visible-transition>
		</div>
		<transition	name="cog-error-message">
			<div v-if="showError" :id="`cog-${$source.id}-error`" class="cog-error-message cog-error-message--container-footer" :aria-live="showLabel ? null : 'polite'" role="alert">{{ errorMessage }}</div>
		</transition>
	</section>
</template>

<script>
	import ValidatedSourcePath from 'src/mixins/validated-source-path';
	import Col from './Col.vue';
	import CSection from './Section.vue';
	import ListTransition from './ListTransition.vue';
	import VisibleTransition from './VisibleTransition.vue';
	import CButton from './Button.vue';
	import recordFocus from 'src/mixins/record-focus';
	import headingDepth from 'src/mixins/heading-depth';
	import showLabel from 'src/mixins/show-label';
	import focusableEls from 'src/focusable-elements';
	import IEx from 'src/assets/ex-outline.svg';
	import IPlus from 'src/assets/plus.svg';
	import { FormEvents } from 'src/framework/public/events';

	export default {
		name: 'CRepeatingSection',
		components: {
			CSection,
			CButton,
			IEx,
			IPlus,
			ListTransition,
			VisibleTransition
		},
		mixins: [ValidatedSourcePath, recordFocus, Col, headingDepth, showLabel],
		inject: ['$resource', 'quantityService', 'flags', 'formEvents'],
		props: {
			hideLabel: Boolean,
			itemLabel: { type: String, required: true },
			addButtonText: { type: String, default: null },
			fieldIndex: { type: String, default: null },
			forceShown: { type: Boolean, default: false },
			hideAddRemoveButtons: { type: Boolean, default: false },
			min: { type: Number, default: 0 },
			max: { type: Number, default: undefined }
		},
		data() {
			return {
				hasNewSection: false,
				hasInteracted: false,
				allowScroll: false
			};
		},
		computed: {
			showLengthError() {
				return this.hasInteracted ? this.$source.conditions?.some(c => c.type.code.includes('ListLength')) : false;
			},
			errorMessage() {
				// use computed firstError instead of using first error off of the source
				return this.$source.firstError ? this.$source.firstError.message : null;
			},
			showError() {
				return !this.form.readonly && (this.showLengthError || this.showValidation);
			},
			showAddButton() {
				if (!this.$source.readonly && !this.max && this.max !== 0)
					return true;
				return !this.$source.readonly && !(this.hideAddRemoveButtons && this.$source.value.length >= this.max);
			},
			showRemoveButton() {
				if (!this.$source.readonly && !this.min)
					return true;
				return !this.$source.readonly && !(this.hideAddRemoveButtons && this.$source.value.length <= this.min);
			}
		},
		created() {
			if (this.formEvents)
				this.formEvents.on(FormEvents.ResetEntry, this.refreshRepeatingSection);
		},
		mounted() {
			if (this.formEvents) {
				this.formEvents.on(FormEvents.ResetEntry, () => {
					this.hasNewSection = false;
				});
			}
		},
		beforeDestroy() {
			if (this.formEvents)
				this.formEvents.off(FormEvents.ResetEntry, this.refreshRepeatingSection);
		},
		methods: {
			triggerErrors() {
				this.hasInteracted = true;
			},
			getRemoveButtonText(itemLabel, itemNumber) {
				return (this.$resource('field-repeatingsection-remove-link-text').includes('{item}') ?
					this.$resource('field-repeatingsection-remove-link-text') :
					this.$resource('field-repeatingsection-remove-link-text') + ' {item}').replace('{item}', itemLabel + ' ' + itemNumber);
			},
			getAddButtonText(itemLabel) {
				return this.addButtonText || this.addButtonText === '' ? this.addButtonText : this.getGeneratedAddButtonText(itemLabel);
			},
			getGeneratedAddButtonText(itemLabel) {
				return (this.$resource('field-repeatingsection-add-link-text').includes('{item}') ?
					this.$resource('field-repeatingsection-add-link-text') :
					this.$resource('field-repeatingsection-add-link-text') + ' {item}').replace('{item}', itemLabel);
			},
			addItem: function(event) {
				this.hasNewSection = true;
				this.lostFocus = true; // validation should now run when adding an item (#17831)
				const list = this.$source.value;
				const JSType = this.$source.property.propertyType;
				this.allowScroll = true;
				// Wait a tick to let it sink in that hasNewSection is now true
				this.$nextTick(()=>{
					list.push(new JSType());
				});

				// Wait until repeating section transition is done (500ms) and then give focus to first field.
				// Don't focus if their are too many sections. For screen readers, the focused field text is read instead of the the error message.
				if (!this.showLengthError) {
					setTimeout(()=>{
						const sections = this.$refs.repeatingSection;

						// Grab first focusable element that is not the remove button
						const firstField = sections[sections.length - 1].$el.querySelectorAll(focusableEls)[1];
						if (firstField)
							firstField.focus();
						this.allowScroll = false;
					}, 500);
				}
				else {
					this.allowScroll = false;
				}
			},
			removeItem: function(event, index) {
				this.recordFocus();

				const list = this.$source.value;
				list.splice(index, 1);
			},
			refreshRepeatingSection() {
				this.hasInteracted = false;
				this.isDirty = false;
				this.lostFocus = false;
				this.detectDirtyEntity();
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-repeating-section {

		&__heading {
			display: inline-block;
			vertical-align: middle;

			.cog-i-diff-addition,
			.cog-i-diff-deletion {
				width: var(--size__icon--diff);
				height: var(--size__icon--diff);
			}
		}

		&__remove-button {
			position: absolute;
			margin-right: -.25em;
			margin-left: -1.75em;
			transform: translateY(-.1em) scale(.8);
		}

		&__add-button {
			max-width: 100%;
			margin-top: $gutter-quarter;
			white-space: nowrap;

			.cog-button__text {
				overflow: hidden;
				text-overflow: ellipsis;
			}

			&:not(.cog-button--no-text) .cog-i-plus {
				margin-left: -.3em;
			}
		}
	}

	.show-remove-button > .cog-section__inner .cog-section__inner--background {
		padding-left: calc(var(--gutter) + var(--h2__font-size, 1em) * .8);
		transition: .3s;
		will-change: padding;
	}

	.hide-remove-button > .cog-section__inner .cog-section__inner--background {
		transition: .3s;
		will-change: padding;
	}
}
</style>
